import React, { useState, useEffect, useCallback } from "react";
import { Lock, RefreshCw, Mail, MailCheck, LogOut, Inbox, Search, X, Reply } from "lucide-react";
import { adminLogin, fetchQuotes, fetchStats, updateQuoteStatus, updateQuoteNotes } from "../api";
import { useToast } from "../hooks/use-toast";

const STATUS_STYLES = {
  new: "bg-amber-100 text-amber-700 border-amber-200",
  in_progress: "bg-blue-100 text-[#155ed6] border-blue-200",
  completed: "bg-green-100 text-green-700 border-green-200",
};
const STATUS_LABEL = { new: "New", in_progress: "In progress", completed: "Completed" };

export default function AdminPage() {
  const [token, setToken] = useState(localStorage.getItem("at_admin_token") || "");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [quotes, setQuotes] = useState([]);
  const [stats, setStats] = useState({ total: 0, new: 0, in_progress: 0, completed: 0 });
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const { toast } = useToast();

  const load = useCallback(async (tk) => {
    setLoading(true);
    try {
      const [q, s] = await Promise.all([fetchQuotes(tk), fetchStats(tk)]);
      setQuotes(q.data);
      setStats(s.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        localStorage.removeItem("at_admin_token");
        setToken("");
        toast({ title: "Session expired", description: "Please log in again." });
      }
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const handleLogin = async (e) => {
    e.preventDefault();
    try {
      const res = await adminLogin(email, password);
      localStorage.setItem("at_admin_token", res.data.token);
      setToken(res.data.token);
      setPassword("");
    } catch (err) {
      toast({ title: "Login failed", description: "Incorrect email or password." });
    }
  };

  const handleStatus = async (id, status) => {
    try {
      const res = await updateQuoteStatus(id, status, token);
      setQuotes((prev) => prev.map((x) => (x.id === id ? res.data : x)));
      load(token);
    } catch (err) {
      toast({ title: "Update failed", description: "Please try again." });
    }
  };

  const handleNotes = async (id, notes) => {
    try {
      const res = await updateQuoteNotes(id, notes, token);
      setQuotes((prev) => prev.map((x) => (x.id === id ? res.data : x)));
      toast({ title: "Note saved" });
    } catch (err) {
      toast({ title: "Note not saved", description: "Please try again." });
    }
  };

  const replySubject = (q) => "Re: Your enquiry to Armstrong Technologies" + (q.service ? ` — ${q.service}` : "");
  const SIGNOFF = "\n\nKind regards,\nArmstrong Technologies\nAmersham | services@armstrongtechnologies.co.uk";
  const replyTemplates = (q) => {
    const n = q.name || "there";
    const svc = q.service || "your requirements";
    return [
      { key: "survey", label: "Book a survey", body: `Hi ${n},\n\nThank you for your enquiry about ${svc}. We'd like to arrange a site survey so we can assess your requirements and prepare an accurate quotation.\n\nCould you let us know a few dates and times that suit you over the coming week?${SIGNOFF}` },
      { key: "quote", label: "Send a quote", body: `Hi ${n},\n\nThank you for your enquiry about ${svc}. Based on the details you provided, please find our quotation below:\n\n[ Add quotation details here ]\n\nIf you'd like to proceed or have any questions, just reply to this email.${SIGNOFF}` },
      { key: "general", label: "General", body: `Hi ${n},\n\nThank you for your enquiry.\n${SIGNOFF}` },
    ];
  };
  const replyHref = (q, body) => `mailto:${q.email}?subject=${encodeURIComponent(replySubject(q))}&body=${encodeURIComponent(body)}`;

  const logout = () => {
    localStorage.removeItem("at_admin_token");
    setToken("");
    setQuotes([]);
  };

  if (!token) {
    return (
      <div className="min-h-[70vh] flex items-center justify-center px-5 bg-[#eaf2ff]">
        <form onSubmit={handleLogin} className="w-full max-w-sm bg-white rounded-3xl border border-black/5 shadow-sm p-8">
          <div className="w-12 h-12 rounded-full bg-[#0b2545] flex items-center justify-center mb-6">
            <Lock className="w-5 h-5 text-white" />
          </div>
          <h1 className="font-display font-extrabold text-[#0b2545] text-2xl">Admin Inbox</h1>
          <p className="mt-2 text-[14px] text-slate-500">Sign in with your email and password to view quote & contact requests.</p>
          <input
            type="email"
            autoFocus
            placeholder="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="mt-6 w-full rounded-xl bg-white border border-slate-200 px-4 py-3.5 text-[15px] focus:outline-none focus:border-[#1e73ff] focus:ring-2 focus:ring-[#1e73ff]/20 transition-all"
          />
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="mt-3 w-full rounded-xl bg-white border border-slate-200 px-4 py-3.5 text-[15px] focus:outline-none focus:border-[#1e73ff] focus:ring-2 focus:ring-[#1e73ff]/20 transition-all"
          />
          <button type="submit" className="mt-4 w-full rounded-full bg-[#1e73ff] text-white shadow-sm shadow-[#1e73ff]/25 text-[15px] font-semibold py-3.5 hover:bg-[#155ed6] transition-colors">
            Log In
          </button>
        </form>
      </div>
    );
  }

  const cards = [
    { label: "Total", value: stats.total, cls: "text-[#0b2545]", key: "all" },
    { label: "New", value: stats.new, cls: "text-amber-600", key: "new" },
    { label: "In progress", value: stats.in_progress, cls: "text-[#155ed6]", key: "in_progress" },
    { label: "Completed", value: stats.completed, cls: "text-green-600", key: "completed" },
  ];

  const filtered = quotes.filter((q) => {
    if (statusFilter !== "all" && q.status !== statusFilter) return false;
    const t = query.trim().toLowerCase();
    if (!t) return true;
    return [q.name, q.email, q.company, q.service, q.message, q.phone]
      .some((v) => (v || "").toLowerCase().includes(t));
  });

  return (
    <div className="min-h-[80vh] bg-[#eaf2ff] py-12">
      <div className="max-w-[1240px] mx-auto px-5 lg:px-8">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-full bg-[#0b2545] flex items-center justify-center">
              <Inbox className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="font-display font-extrabold text-[#0b2545] text-2xl">Quote & Contact Inbox</h1>
              <p className="text-[13px] text-slate-500">Submissions from the website forms</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => load(token)} className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-[#0b2545] text-[14px] font-semibold px-4 py-2.5 hover:bg-slate-100 transition-colors">
              <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} /> Refresh
            </button>
            <button onClick={logout} className="inline-flex items-center gap-2 rounded-full bg-[#0b2545] text-white text-[14px] font-semibold px-4 py-2.5 hover:bg-[#12355f] transition-colors">
              <LogOut className="w-4 h-4" /> Log out
            </button>
          </div>
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
          {cards.map((c) => (
            <button
              key={c.label}
              onClick={() => setStatusFilter(c.key)}
              data-testid={`stat-filter-${c.key}`}
              className={`text-left bg-white rounded-2xl border p-5 transition-all ${statusFilter === c.key ? "border-[#1e73ff] ring-2 ring-[#1e73ff]/20" : "border-black/5 hover:border-slate-300"}`}
            >
              <p className="text-[12px] font-semibold tracking-[0.15em] uppercase text-slate-400">{c.label}</p>
              <p className={`mt-2 font-display font-extrabold text-3xl ${c.cls}`}>{c.value}</p>
            </button>
          ))}
        </div>

        <div className="mt-6 flex items-center gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px]">
            <Search className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, company, service or message…"
              data-testid="inbox-search-input"
              className="w-full rounded-full bg-white border border-slate-200 pl-11 pr-4 py-3 text-[14px] focus:outline-none focus:border-[#1e73ff] focus:ring-2 focus:ring-[#1e73ff]/20 transition-all"
            />
          </div>
          {(query || statusFilter !== "all") && (
            <button
              onClick={() => { setQuery(""); setStatusFilter("all"); }}
              data-testid="inbox-clear-filters"
              className="inline-flex items-center gap-2 rounded-full bg-white border border-slate-200 text-slate-600 text-[13px] font-semibold px-4 py-3 hover:bg-slate-100 transition-colors"
            >
              <X className="w-4 h-4" /> Clear filters
            </button>
          )}
          <span className="text-[13px] text-slate-500" data-testid="inbox-result-count">
            {filtered.length} of {quotes.length}
          </span>
        </div>

        <div className="mt-8 bg-white rounded-2xl border border-black/5 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left text-[14px]">
              <thead>
                <tr className="bg-[#0b2545] text-slate-200 text-[11px] uppercase tracking-[0.12em]">
                  <th className="px-5 py-4 font-semibold">Received</th>
                  <th className="px-5 py-4 font-semibold">Name</th>
                  <th className="px-5 py-4 font-semibold">Contact</th>
                  <th className="px-5 py-4 font-semibold">Service</th>
                  <th className="px-5 py-4 font-semibold">Message</th>
                  <th className="px-5 py-4 font-semibold">Src</th>
                  <th className="px-5 py-4 font-semibold">Email</th>
                  <th className="px-5 py-4 font-semibold">Status</th>
                  <th className="px-5 py-4 font-semibold">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-black/5">
                {filtered.length === 0 && (
                  <tr><td colSpan={9} className="px-5 py-12 text-center text-slate-400">{quotes.length === 0 ? "No submissions yet." : "No submissions match your filters."}</td></tr>
                )}
                {filtered.map((q) => (
                  <tr key={q.id} className="hover:bg-slate-50 align-top">
                    <td className="px-5 py-4 text-slate-500 whitespace-nowrap">{new Date(q.created_at).toLocaleString("en-GB", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" })}</td>
                    <td className="px-5 py-4 font-semibold text-[#0b2545] whitespace-nowrap">{q.name}{q.company ? <span className="block text-[12px] font-normal text-slate-400">{q.company}</span> : null}</td>
                    <td className="px-5 py-4 text-slate-600 whitespace-nowrap">
                      <a href={`mailto:${q.email}`} className="hover:text-[#14b8a6]">{q.email}</a>
                      {q.phone ? <span className="block text-[12px] text-slate-400">{q.phone}</span> : null}
                      <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
                        <span className="text-slate-400 inline-flex items-center gap-1"><Reply className="w-3.5 h-3.5" /> Reply:</span>
                        {replyTemplates(q).map((t) => (
                          <a
                            key={t.key}
                            href={replyHref(q, t.body)}
                            data-testid={`reply-${t.key}-${q.id}`}
                            className="font-semibold text-[#1e73ff] hover:text-[#155ed6] whitespace-nowrap"
                          >
                            {t.label}
                          </a>
                        ))}
                      </div>
                    </td>
                    <td className="px-5 py-4 text-slate-600 whitespace-nowrap">{q.service || "—"}</td>
                    <td className="px-5 py-4 text-slate-600 max-w-[280px]"><span className="line-clamp-3">{q.message || "—"}</span></td>
                    <td className="px-5 py-4 text-slate-500 capitalize">{q.source}</td>
                    <td className="px-5 py-4">
                      {q.email_sent
                        ? <span className="inline-flex items-center gap-1 text-green-600 text-[12px]"><MailCheck className="w-4 h-4" /> Sent</span>
                        : <span className="inline-flex items-center gap-1 text-slate-400 text-[12px]"><Mail className="w-4 h-4" /> Stored</span>}
                    </td>
                    <td className="px-5 py-4">
                      <select
                        value={q.status}
                        onChange={(e) => handleStatus(q.id, e.target.value)}
                        className={`rounded-full border px-3 py-1.5 text-[12px] font-semibold cursor-pointer focus:outline-none ${STATUS_STYLES[q.status] || ""}`}
                      >
                        {Object.keys(STATUS_LABEL).map((s) => (
                          <option key={s} value={s}>{STATUS_LABEL[s]}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <textarea
                        defaultValue={q.notes || ""}
                        onBlur={(e) => { if (e.target.value !== (q.notes || "")) handleNotes(q.id, e.target.value); }}
                        placeholder="Add a note…"
                        data-testid={`note-input-${q.id}`}
                        className="w-[200px] min-h-[52px] rounded-lg border border-slate-200 px-2.5 py-1.5 text-[12px] text-slate-700 focus:outline-none focus:border-[#1e73ff] focus:ring-1 focus:ring-[#1e73ff]/30 resize-y"
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
